import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import { useNavigate } from "react-router-dom";
import useWebSocket, { ReadyState } from "react-use-websocket";

import { ChatMessage, ProcessingStepMessage } from "@/components/chat/types";
import {
  appendToChatMessages,
  getLoadingMessage,
  isProcessingStep,
  mapToRenderMessage,
} from "@/components/chat/utils";
import {
  ChatResponse,
  ChatSectionStatus,
  NewConvoResponse,
  ProcessingStepSection,
} from "@/types/chat-response";
import { UserObject } from "@/types/user";

import { useChatHistory } from "./ChatHistoryContext";
import { useNotifications } from "./NotificationsContext";
import { usePanelContent } from "./PanelContentContext";

export interface ChatContextType {
  conversationId: string;
  title: string;
  messages: ChatMessage[];
  processingSteps: ProcessingStepMessage[];
  chatLoading: boolean;
  previousChatLoading: boolean;
  connected: boolean;
  sendToChatbot: (message: ChatMessage) => void;
  updateWithConversationId: (id: string | null) => void;
}

const ChatContext = createContext<ChatContextType | undefined>(undefined);

/**
 * Keep-alive frame. The WebSocket API routes on the `action` field of the
 * frame body and has no catch-all route, so the payload has to be JSON shaped
 * like any other request — the library's default bare `"ping"` string would be
 * unroutable and take the connection down with it.
 */
export const HEARTBEAT_MESSAGE = JSON.stringify({ action: "ping" });

/**
 * Body the keep-alive route replies with. Naming it here lets the socket hook
 * recognise the reply, refresh its liveness timer, and drop the frame instead
 * of surfacing it as chat content.
 */
export const HEARTBEAT_RETURN_MESSAGE = "pong";

/**
 * How often to ping an otherwise idle connection. Well inside the API's ten
 * minute idle timeout while adding only two frames a minute.
 */
export const HEARTBEAT_INTERVAL_MS = 30_000;

/**
 * How long without any inbound frame before the connection is treated as dead.
 * Deliberately a multiple of the interval: a single late reply is normal, so
 * closing on the first one would churn connections rather than steady them.
 */
export const HEARTBEAT_TIMEOUT_MS = 90_000;

/** Reconnection attempts before the socket hook gives up on its own. */
export const RECONNECT_ATTEMPTS = 10;

/** Longest gap between reconnection attempts, which back off exponentially. */
export const MAX_RECONNECT_INTERVAL_MS = 30_000;

interface ChatProviderProps {
  getToken: () => Promise<string>;
  user: UserObject;
  children: React.ReactNode;
}

export const ChatProvider: React.FC<ChatProviderProps> = ({
  getToken,
  user,
  children,
}) => {
  const navigate = useNavigate();
  const [conversationId, setConversationId] = useState<string>("");
  const [title, setTitle] = useState<string>("New Chat");
  const [messages, setChatMessages] = useState<ChatMessage[]>([]);
  const [processingSteps, setProcessingSteps] = useState<
    ProcessingStepMessage[]
  >([]);
  const [previousChatLoading, setPreviousChatLoading] = useState(false);
  const [chatLoading, setChatLoading] = useState(false);

  // Whether the socket hook is allowed to hold a connection. Normally true;
  // driven briefly to false to recover from an exhausted retry budget (see
  // resumeConnection).
  const [socketEnabled, setSocketEnabled] = useState(true);

  const { toggleSplitPanel } = usePanelContent();
  const { getChatById, loadChatHistory } = useChatHistory();

  const { notifyError } = useNotifications();

  // Held in a ref because it is only ever called imperatively, never rendered.
  // Reading it through a ref also keeps the URL getter's identity stable: the
  // socket hook reconnects whenever that identity changes, so depending on a
  // notifier that was not memoised would silently churn connections.
  const notifyErrorRef = useRef(notifyError);
  notifyErrorRef.current = notifyError;

  // Guards against repeating the expired-session notice. Every resume attempt
  // re-runs the URL getter, and a user switching tabs a few times would
  // otherwise stack an identical toast each time. Cleared once a connection
  // succeeds, so a genuinely new expiry is reported again. A ref rather than
  // state: it gates an imperative call and is never rendered.
  const sessionExpiredNotifiedRef = useRef(false);

  // Build the WebSocket URL with a fresh token on every connection attempt.
  // react-use-websocket calls this function each time it (re)connects,
  // ensuring the Cognito ID token is never stale.
  const getSocketUrl = useCallback(async () => {
    const baseUrl =
      import.meta.env.VITE_WS_ENDPOINT || "ws://localhost:5000/ws";

    let freshToken: string;
    try {
      freshToken = await getToken();
    } catch {
      freshToken = "";
    }

    if (!freshToken) {
      // No token means the sign-in session is gone, not that the network is
      // unhappy. The auth library reports this by resolving without a token as
      // well as by rejecting, so both arrive here. Throwing abandons the
      // attempt: retrying a handshake the authorizer must reject would burn
      // the whole retry budget and still leave the user uninformed.
      if (!sessionExpiredNotifiedRef.current) {
        sessionExpiredNotifiedRef.current = true;
        notifyErrorRef.current(
          "Your session has expired.",
          "Reload the page to sign in again and continue chatting."
        );
      }
      throw new Error("Cannot open a chat connection without a valid token");
    }

    return `${baseUrl}?token=${encodeURIComponent(freshToken)}`;
  }, [getToken]);

  const { sendJsonMessage, lastJsonMessage, readyState } = useWebSocket(
    getSocketUrl,
    {
      onOpen: () => {
        // A later expiry is a new episode and worth reporting again.
        sessionExpiredNotifiedRef.current = false;
      },
      onError: () => {
        // Deliberately silent. A failure here is followed by a scheduled
        // reconnect, so warning the user invites them to act on something that
        // resolves itself. The unrecoverable case is reported from
        // onReconnectStop instead.
      },
      onReconnectStop: () => {
        // The hook has stopped retrying, so nothing further happens without
        // either an explicit resume or a reload. Worth interrupting the user
        // for, unlike the errors on the way here.
        notifyErrorRef.current(
          "Chat is disconnected.",
          "Reconnection attempts stopped. Switch back to this tab or reload the page to try again."
        );
      },
      shouldReconnect: () => {
        // Always attempt to reconnect — the fresh token getter ensures
        // we won't keep retrying with an expired credential.
        return true;
      },
      reconnectAttempts: RECONNECT_ATTEMPTS,
      reconnectInterval: (attemptNumber: number) =>
        Math.min(1000 * 2 ** attemptNumber, MAX_RECONNECT_INTERVAL_MS),
      heartbeat: {
        message: HEARTBEAT_MESSAGE,
        returnMessage: HEARTBEAT_RETURN_MESSAGE,
        interval: HEARTBEAT_INTERVAL_MS,
        timeout: HEARTBEAT_TIMEOUT_MS,
      },
    },
    socketEnabled
  );

  // Derived rather than tracked through the open/close callbacks: the socket's
  // own ready state cannot drift out of step with the socket, whereas a
  // separate flag silently does whenever a transition arrives without its
  // callback (a deliberate teardown, an aborted connection attempt).
  const connected = readyState === ReadyState.OPEN;

  /**
   * Give the socket a fresh retry budget and connect again.
   *
   * The socket hook stops permanently after `reconnectAttempts` failures — a
   * suspended laptop or a few minutes without network is enough to spend the
   * budget, after which the tab stays disconnected however long it is left
   * open. The hook resets its attempt counter when asked not to connect, so
   * briefly disabling it is the supported way back; the effect below restores
   * it on the following commit, once the counter has been cleared.
   */
  const resumeConnection = useCallback(() => {
    setSocketEnabled(false);
  }, []);

  useEffect(() => {
    if (!socketEnabled) {
      setSocketEnabled(true);
    }
  }, [socketEnabled]);

  // Reconnect when the user comes back to a tab that lost its connection, or
  // when the browser reports the network has returned. These are the moments a
  // dead socket can plausibly succeed again, and they cost nothing while the
  // connection is healthy.
  useEffect(() => {
    const resumeIfDropped = () => {
      // Never disturb a live connection: tearing it down would abandon an
      // answer already in flight.
      if (readyState === ReadyState.OPEN) {
        return;
      }
      resumeConnection();
    };

    const handleVisibilityChange = () => {
      if (document.visibilityState !== "visible") {
        return;
      }
      resumeIfDropped();
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);
    window.addEventListener("online", resumeIfDropped);

    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
      window.removeEventListener("online", resumeIfDropped);
    };
  }, [readyState, resumeConnection]);

  useEffect(() => {
    const response = lastJsonMessage as ChatResponse;
    if (response) {
      if (isProcessingStep(response)) {
        setProcessingSteps((prevSteps) => {
          const newStep: ProcessingStepMessage = {
            content: response.response,
            timestamp: new Date().toISOString(),
            section: response.section as ProcessingStepSection,
            seq: response.seq,
          };

          // If the message has a seq number, insert in sorted order.
          // This handles out-of-order delivery caused by the OTel
          // BatchSpanProcessor worker thread flushing asynchronously.
          if (newStep.seq != null) {
            const updated = [...prevSteps, newStep];
            updated.sort((a, b) => (a.seq ?? 0) - (b.seq ?? 0));
            return updated;
          }

          // Fallback for messages without seq (backward compatibility)
          return [...prevSteps, newStep];
        });
      } else {
        if (response.section_status === ChatSectionStatus.NEW_CONVO) {
          const { conversation_id, title } = JSON.parse(
            response.response
          ) as NewConvoResponse;
          setConversationId(conversation_id);
          setTitle(title);
          navigate(`/?thread=${conversation_id}`);
          loadChatHistory();
        } else {
          setChatMessages(appendToChatMessages(response));
        }

        if (response.final) {
          setChatLoading(false);
        }
      }
    }
  }, [lastJsonMessage]);

  const sendToChatbot = useCallback(
    (message: ChatMessage) => {
      setProcessingSteps([]);
      setChatMessages((prevMessages) => [
        ...prevMessages,
        message,
        getLoadingMessage(toggleSplitPanel),
      ]);
      setChatLoading(true);
      sendJsonMessage({
        action: "chat",
        message: message.content,
        conversation_id: conversationId,
        user_id: user.userId,
      });
    },
    [sendJsonMessage, conversationId, user.userId]
  );

  const updateWithConversationId = useCallback(
    async (id: string | null) => {
      if (id === null) {
        setConversationId("");
        setTitle("New Chat");
        setChatMessages([]);
        return;
      }

      setPreviousChatLoading(true);
      const response = await getChatById(id);

      if (response) {
        const { title, messages } = response;
        setTitle(title);
        setConversationId(id);
        setChatMessages(messages.map(mapToRenderMessage(user)));
        // Clear out steps.
        setProcessingSteps([]);
      }

      setPreviousChatLoading(false);
    },
    [user]
  );

  return (
    <ChatContext.Provider
      value={{
        conversationId,
        title,
        messages,
        processingSteps,
        chatLoading,
        previousChatLoading,
        connected,
        sendToChatbot,
        updateWithConversationId,
      }}
    >
      {children}
    </ChatContext.Provider>
  );
};

export const useChat = () => {
  const context = useContext(ChatContext);
  if (context === undefined) {
    throw new Error("useChat must be used within a ChatProvider");
  }
  return context;
};
